import { version as clientVersion } from '@/config';

export type AppVersionInfo = {
  version: string;
};

const DISMISSED_VERSION_KEY = 'app_update_dismissed_version';

export const getClientAppVersion = (): string => clientVersion;

export const normalizeVersion = (value?: string | null): string =>
  (value ?? '').trim().replace(/^v/i, '');

/** Returns positive if a > b, 0 if equal, negative if a < b */
export const compareSemver = (a: string, b: string): number => {
  const left = normalizeVersion(a)
    .split('.')
    .map(part => Number.parseInt(part, 10) || 0);
  const right = normalizeVersion(b)
    .split('.')
    .map(part => Number.parseInt(part, 10) || 0);
  const length = Math.max(left.length, right.length);

  for (let index = 0; index < length; index += 1) {
    const diff = (left[index] ?? 0) - (right[index] ?? 0);
    if (diff !== 0) return diff;
  }

  return 0;
};

export const isServerVersionNewer = (
  serverVersion: string,
  currentVersion: string = getClientAppVersion()
): boolean => compareSemver(serverVersion, currentVersion) > 0;

export const getDismissedUpdateVersion = (): string | null => {
  try {
    return sessionStorage.getItem(DISMISSED_VERSION_KEY);
  } catch {
    return null;
  }
};

export const setDismissedUpdateVersion = (version: string) => {
  try {
    sessionStorage.setItem(DISMISSED_VERSION_KEY, normalizeVersion(version));
  } catch {
    // ignore storage failures
  }
};

export const shouldShowUpdateForVersion = (serverVersion: string): boolean => {
  const normalizedServer = normalizeVersion(serverVersion);
  if (!normalizedServer) return false;
  if (!isServerVersionNewer(normalizedServer)) return false;

  const dismissed = getDismissedUpdateVersion();
  if (dismissed && normalizeVersion(dismissed) === normalizedServer) {
    return false;
  }

  return true;
};

const resolveVersionUrl = (): string => {
  const publicUrl = import.meta.env.VITE_PUBLIC_URL;
  const base =
    typeof publicUrl === 'string' && publicUrl.length > 0
      ? publicUrl
      : import.meta.env.BASE_URL || '/';
  const normalized = base.endsWith('/') ? base : `${base}/`;
  return `${normalized}version.json`;
};

export const fetchServerAppVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${resolveVersionUrl()}?_=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) return null;

    const data = (await response.json()) as AppVersionInfo;
    const version = normalizeVersion(data?.version);
    return version || null;
  } catch {
    return null;
  }
};
